console.log('login-page.component.ts is running');
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { UserAccount } from '../user-account';
import { AccountService } from '../account-service.service';
import { GuidUtil } from '../utils/guid-util';
import * as CryptoJS from 'crypto-js';

import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '../app.component';
import { provideHttpClient } from '@angular/common/http';
import { LanguageService } from '../language.service';
//import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(),
    // Other providers here
  ],
}).catch((err) => console.error(err));

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    // TODO: `HttpClientModule` should not be imported into a component directly.
    // Please refactor the code to add `provideHttpClient()` call to the provider list in the
    // application bootstrap logic and remove the `HttpClientModule` import from this component.
    //HttpClientModule,
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    //  MatIconModule,
    TranslateModule,
  ],
  providers: [AccountService],
  template: `<form [formGroup]="applyForm" (ngSubmit)="login()">
    <div class="mobile-login-container">
      <!-- Language Icon with Dropdown -->
      <div class="language-icon-container">
        <button class="icon-button" (click)="toggleLanguageDropdown()">
          <img src="/assets/icons/language.svg" alt="Language Icon" />
        </button>
        <div class="language-dropdown" [class.show]="showLanguageDropdown">
          <div
            *ngFor="let lang of languages"
            (click)="switchLanguage(lang.code)"
          >
            {{ lang.label }}
          </div>
        </div>
      </div>
      <p class="login-title">{{ 'login-page.TITLE' | translate }}</p>

      <!-- Email Input -->
      <div class="rounded-input">
        <input
          type="text"
          [attr.placeholder]="'login-page.EMAIL_PLACEHOLDER' | translate"
          formControlName="emailControl"
        />
        <div
          *ngIf="
            applyForm.get('emailControl')?.invalid &&
            (applyForm.get('emailControl')?.touched ||
              applyForm.get('emailControl')?.dirty)
          "
          class="error-message"
        >
          {{ 'login-page.EMAIL_ERROR' | translate }}
        </div>
      </div>

      <!-- Password Input with Eye Icon -->
      <div class="rounded-input password-container">
        <input
          [type]="showPassword ? 'text' : 'password'"
          [attr.placeholder]="'login-page.PASSWORD_PLACEHOLDER' | translate"
          formControlName="passwordControl"
          [dir]="currentDirection_"
        />
        <button
          type="button"
          class="eye-icon"
          (click)="togglePasswordVisibility()"
        >
          <img
            *ngIf="showPassword"
            src="assets/icons/eye-off.svg"
            alt="Hide Password"
          />
          <img
            *ngIf="!showPassword"
            src="assets/icons/eye.svg"
            alt="Show Password"
          />
        </button>
      </div>

      <!-- Password Validation Message -->
      <div
        *ngIf="
          applyForm.get('passwordControl')?.invalid &&
          (applyForm.get('passwordControl')?.touched ||
            applyForm.get('passwordControl')?.dirty)
        "
        class="error-message"
      >
        {{ 'login-page.PASSWORD_ERROR' | translate }}
      </div>

      <button
        type="submit"
        [disabled]="applyForm.invalid"
        class="rounded-button"
      >
        {{ 'login-page.LOGIN_BUTTON' | translate }}
      </button>

      <p class="forgot-password-link">
        {{ 'login-page.FORGOT_PASSWORD' | translate }}
      </p>
      <p class="connect-with-text">
        {{ 'login-page.CONNECT_WITH' | translate }}
      </p>

      <!-- Social Login Buttons -->
      <div class="social-buttons-container">
        <button class="rounded-button-facebook">
          {{ 'login-page.FACEBOOK' | translate }}
        </button>
        <button class="rounded-button-gmail">
          {{ 'login-page.GMAIL' | translate }}
        </button>
      </div>

      <p class="sign-up-link">
        {{ 'login-page.NO_ACCOUNT' | translate }}
        <a [routerLink]="['sign-up-page']">
          {{ 'login-page.SIGN_UP' | translate }}</a
        >
      </p>

      <!-- General Error Message -->
      <p *ngIf="errorMessage" class="error-message">
        {{ errorMessage }}
        <a
          *ngIf="showSignUpLink"
          (click)="redirectToSignUp()"
          class="sign-up-link"
          >{{ 'login-page.SIGN_UP_LINK' | translate }}</a
        >
      </p>
    </div>
  </form> `,
  styleUrls: ['./login-page.component.css'],
})
export class LoginPageComponent {
  userAccount!: UserAccount;
  protected uri: string = 'login';
  errorMessage: string = '';
  showSignUpLink: boolean = false; // Add this to conditionally show the "Sign up" link

  showLanguageDropdown = false;
  languages = this.languageService.getLanguages();

  applyForm = new FormGroup({
    emailControl: new FormControl('', [Validators.required, Validators.email]),
    passwordControl: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
    ]),
  });

  constructor(
    private accountService: AccountService,
    private http: HttpClient,
    private router: Router,
    private languageService: LanguageService,
  ) {
    console.log('LoginPageComponent constructor called');
    this.userAccount = {
      id: '',
      unique_id: '',
      email: '',
      name: '',
      last_name: '',
      latin_name: '',
      latin_lastname: '',
      birthdate: '',
      country: '',
      city: '',
      mobile_number: '',
      nationality: '',
      gender: 0,
      is_activated: false,
      is_premium: false,
      activation_code: 0,
      is_verified: false,
      is_mobileVerified: false,
      profile_completed: false,
      last_login: '',
      registration_date: '',
      password: '',
      confirm_password: '',
      authentication: '',
      verificationCode: '',
    };
  }
  currentDirection_: string = 'ltr'; // Default to LTR
  ngOnInit(): void {
    console.log('LoginPageComponent initialized');
  }

  login() {
    if (this.applyForm.invalid) {
      return;
    }

    this.userAccount.email = (
      this.applyForm.value.emailControl ?? ''
    ).toLowerCase();
    this.userAccount.password = this.hashPassword(
      this.applyForm.value.passwordControl ?? '',
    );

    this.accountService.run(this.userAccount, this.uri).subscribe({
      next: (response) => {
        if (response.status === '200') {
          // Successful login
          const authenticationToken = response.authentication;
          const is_verified: boolean = JSON.parse(
            response.is_verified ?? 'false',
          );

          const loginKey = GuidUtil.generateGuid();
          // Store authentication token securely
          localStorage.setItem(loginKey, authenticationToken);
          localStorage.setItem('loginKey', loginKey);
          localStorage.setItem('email', this.userAccount.email);
          if (!is_verified) {
            this.router.navigate(['verify-email']);
          } else {
            this.router.navigate(['available-clans']);
          }
          // Redirect to a protected route
        } else if (response.status === '401') {
          // Invalid password
          this.errorMessage = response.message;
          this.showSignUpLink = false;
        } else if (response.status === '404') {
          // Email not found
          this.errorMessage = response.message;
          this.showSignUpLink = true; // Show "Sign up" link for 404 errors
        } else {
          // Handle unexpected status
          this.errorMessage =
            'An unexpected error occurred. Please try again later.';
          this.showSignUpLink = false;
        }
      },
      error: (error) => {
        // In case of network or server errors (500, etc.)
        this.errorMessage = 'A network error occurred. Please try again later.';
        this.showSignUpLink = false;
      },
      complete: () => {
        // Optional: Handle completion if needed
      },
    });
  }

  redirectToSignUp() {
    this.router.navigate(['sign-up-page']); // Redirect to the sign-up page
  }

  hashPassword(password: string): string {
    // Hash the password using SHA-256
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
  }

  toggleLanguageDropdown() {
    this.showLanguageDropdown = !this.showLanguageDropdown;
  }

  switchLanguage(language: string) {
    this.languageService.setLanguage(language); // Handles setting the language and direction
    this.showLanguageDropdown = false;
    this.currentDirection_ = ['ar', 'fa'].includes(language) ? 'rtl' : 'ltr';
  }

  showPassword = false; // Toggle state

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
